import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import PublicNavBar from "../components/PublicNavBar";
import PublicFooter from "../components/PublicFooter";
import {
  Briefcase, Award, TrendingUp, BookOpen, CheckCircle2, ArrowRight,
  Target, Sparkles, Building2, Users2, ShieldCheck, GraduationCap,
} from "lucide-react";
import usePageTitle from "../hooks/usePageTitle";

/**
 * Public marketing page for Working Professionals.
 *
 * Mirrors the architecture of StudentsPublic / CompaniesPublic but is
 * tuned for a working-professional audience: career switch, lateral
 * moves, upskilling, verified certifications, and recruiter visibility.
 *
 * Sign-up CTA: /signup?role=student&track=professional — sub_role
 * defaults to "professional" so the backend tags the user accordingly.
 */
export default function ProfessionalsPublic() {
  usePageTitle("For Working Professionals · SkillConnect");
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, []);

  return (
    <div className="min-h-screen bg-white text-ink" data-testid="professionals-page">
      <PublicNavBar primaryCta={{
        to: "/signup?role=student&track=professional",
        label: "Join as professional",
      }} />

      {/* ─────────────── Hero ─────────────── */}
      <section className="relative overflow-hidden border-b border-rule">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-emerald-50/40 -z-10"/>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="eyebrow inline-flex items-center gap-1.5">
              <Briefcase size={11}/> For working professionals
            </span>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl mt-2 leading-[1.05]">
              Level up your career — <span className="text-signal">on your own terms.</span>
            </h1>
            <p className="text-muted text-base lg:text-lg mt-5 max-w-2xl leading-relaxed">
              Whether you&apos;re looking for your <b>next role</b>, planning a <b>career switch</b>, or
              <b> upskilling for a promotion</b> — SkillConnect gives you the same hiring engine that
              powers India&apos;s top placement cells, tuned for mid-career professionals.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/signup?role=student&track=professional" className="btn-primary"
                    data-testid="hero-cta-signup-professional">
                Join as professional <ArrowRight size={14}/>
              </Link>
              <Link to="/opportunities" className="btn-secondary" data-testid="hero-cta-browse">
                Browse opportunities
              </Link>
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-muted">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={12} className="text-emerald-600"/> 100% free for individuals</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={12} className="text-emerald-600"/> Verified certifications</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={12} className="text-emerald-600"/> Direct recruiter access</span>
            </div>
          </div>

          {/* KPI / proof card */}
          <div className="lg:col-span-5">
            <div className="card card-pad bg-white border-2 border-default/10 shadow-card relative overflow-hidden">
              <div className="absolute -right-12 -top-12 w-40 h-40 bg-signal/5 rounded-full"/>
              <div className="absolute -right-6 -bottom-6 w-28 h-28 bg-emerald-100 rounded-full opacity-50"/>
              <div className="relative">
                <span className="eyebrow">Built for mid-career growth</span>
                <div className="mt-3 space-y-4">
                  <Kpi v="3 yrs" l="Average experience of pros on the platform" tone="signal"/>
                  <Kpi v="1.6×"  l="Faster offer-letter turnaround vs. cold applying"/>
                  <Kpi v="₹4.2 L" l="Median CTC bump on a role switch via SkillConnect"/>
                  <Kpi v="38"   l="Verified CoE Labs you can certify in"/>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────── What you get ─────────────── */}
      <section className="border-b border-rule bg-paper">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-14 lg:py-20">
          <span className="eyebrow">What you get</span>
          <h2 className="font-display font-bold text-3xl lg:text-4xl mt-2 max-w-3xl">
            Everything you need to find your next career move.
          </h2>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <Feature
              icon={Target}
              title="Curated lateral roles"
              copy="Roles tagged by years-of-experience and tech stack. No spammy fresher posts to wade through."
            />
            <Feature
              icon={Award}
              title="Verified CoE certifications"
              copy="Pass an assessment under proctoring → blockchain-verifiable certificate that recruiters can trust."
            />
            <Feature
              icon={Sparkles}
              title="Resume scoring with AI"
              copy="See exactly how your resume stacks against a posting — and what to add / cut to improve match."
            />
            <Feature
              icon={BookOpen}
              title="Self-paced learning paths"
              copy="2-4 hour bite-sized modules built around CoE Labs. Earn badges that show up on your public profile."
            />
            <Feature
              icon={ShieldCheck}
              title="Private to recruiters"
              copy="Your current employer never sees your profile in search results unless you opt in."
            />
            <Feature
              icon={TrendingUp}
              title="Salary insights"
              copy="See market-rate CTC ranges per role + city before you negotiate."
            />
          </div>
        </div>
      </section>

      {/* ─────────────── Who it's for ─────────────── */}
      <section className="border-b border-rule">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-14 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <span className="eyebrow">Who this is for</span>
              <h2 className="font-display font-bold text-3xl lg:text-4xl mt-2">
                Five career moments. One platform.
              </h2>
              <p className="text-muted mt-4 leading-relaxed">
                We&apos;ve seen these patterns across thousands of professionals on the platform.
                Pick the one that matches you — sign-up takes 30 seconds.
              </p>
            </div>
            <div className="space-y-3">
              <Persona n="01"
                title="The career switcher"
                copy="You&apos;re in QA / Support / Ops and want to move into Dev / Data / Cloud. Start with a CoE Lab, earn a certification, then apply."/>
              <Persona n="02"
                title="The mid-career upgrader"
                copy="3–6 yrs experience. You want a tech stack upgrade and a 30% CTC jump in your next switch."/>
              <Persona n="03"
                title="The returnship candidate"
                copy="Returning from a maternity / sabbatical break. We surface returnship-friendly roles + refresh paths."/>
              <Persona n="04"
                title="The senior IC"
                copy="Staff / Principal Engineer hunting for greenfield problems. Skip the JD-spam — we show vetted senior roles only."/>
              <Persona n="05"
                title="The future-founder"
                copy="Want to learn AI / GenAI / FinTech before quitting? Our CoE Labs are run by domain practitioners."/>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────── How it works ─────────────── */}
      <section className="border-b border-rule bg-paper">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-14 lg:py-20">
          <span className="eyebrow">How it works</span>
          <h2 className="font-display font-bold text-3xl lg:text-4xl mt-2">Four steps. One week.</h2>
          <ol className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <Step n="1" title="Sign up" copy="Pick &quot;Working Professional&quot; on signup. Tell us your current role, company, and 3 target skills."/>
            <Step n="2" title="Pick a CoE Lab" copy="Choose from 10 industry verticals. Each lab = 4–8 weeks of self-paced learning + a capstone."/>
            <Step n="3" title="Get certified" copy="Pass a proctored assessment. Earn a verifiable certificate that shows up on your public profile."/>
            <Step n="4" title="Get matched" copy="Apply to vetted roles or get pinged by recruiters who searched for your skill set."/>
          </ol>
        </div>
      </section>

      {/* ─────────────── Social proof ─────────────── */}
      <section className="border-b border-rule">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-14 lg:py-20">
          <div className="grid lg:grid-cols-3 gap-6">
            <Quote
              name="Pranav Kulkarni"
              role="SDE-2 · Razorpay"
              from="Was: QA Engineer at HCL"
              quote="The CoE Lab on AI Engineering plus the proctored cert was the trigger. I had 3 offers in 5 weeks; the median CTC delta was 70%."
            />
            <Quote
              name="Sneha Iyer"
              role="Data Engineer · Swiggy"
              from="Was: BI Analyst at Cognizant"
              quote="Recruiters reached out within 10 days of my Data CoE badge going public. Salary insights helped me anchor the offer well."
            />
            <Quote
              name="Anuj Mahajan"
              role="Cloud Architect · DBS Bank"
              from="Was: Returning after 16-month sabbatical"
              quote="The returnship cohort + refresh paths got me back into shipping mode. Hiring managers actually read the certificates."
            />
          </div>
        </div>
      </section>

      {/* ─────────────── Final CTA ─────────────── */}
      <section className="bg-default text-paper">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-16 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <span className="eyebrow text-paper/70">Start today</span>
            <h2 className="font-display font-black text-3xl lg:text-4xl mt-2 leading-tight">
              Your next role is a lab + a certificate away.
            </h2>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link to="/signup?role=student&track=professional"
                  className="btn-primary bg-paper text-default hover:bg-paper/90 border-paper"
                  data-testid="final-cta-signup">
              Join as professional <ArrowRight size={14}/>
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

/* ────────────────── small presentational helpers ────────────────── */

function Kpi({ v, l, tone }) {
  const toneCls = tone === "signal" ? "text-signal" : "text-default";
  return (
    <div className="flex items-baseline gap-3">
      <div className={`font-display font-black text-3xl whitespace-nowrap shrink-0 ${toneCls}`}>{v}</div>
      <div className="text-sm text-muted leading-snug">{l}</div>
    </div>
  );
}

function Feature({ icon: Icon, title, copy }) {
  return (
    <div className="card card-pad bg-white border-rule hover:border-signal/40 transition group">
      <div className="rounded-sm bg-signal/10 text-signal w-9 h-9 flex items-center justify-center mb-3 group-hover:scale-110 transition">
        <Icon size={16}/>
      </div>
      <div className="font-display font-bold text-lg">{title}</div>
      <p className="text-sm text-muted mt-1.5 leading-relaxed">{copy}</p>
    </div>
  );
}

function Persona({ n, title, copy }) {
  return (
    <div className="card card-pad bg-white flex items-start gap-3 hover:border-signal/40 transition">
      <div className="font-mono text-xs font-bold text-signal bg-signal/10 px-2 py-1 rounded-sm">{n}</div>
      <div className="min-w-0">
        <div className="font-display font-bold">{title}</div>
        <p className="text-sm text-muted mt-1 leading-relaxed">{copy}</p>
      </div>
    </div>
  );
}

function Step({ n, title, copy }) {
  return (
    <li className="card card-pad bg-white relative">
      <div className="absolute -top-3 left-4 bg-signal text-white font-mono text-xs font-bold px-2 py-1 rounded-sm shadow">{n}</div>
      <div className="font-display font-bold text-lg mt-2">{title}</div>
      <p className="text-sm text-muted mt-1.5 leading-relaxed">{copy}</p>
    </li>
  );
}

function Quote({ name, role, from, quote }) {
  return (
    <figure className="card card-pad bg-white">
      <blockquote className="text-default leading-relaxed">“{quote}”</blockquote>
      <figcaption className="mt-4 pt-4 border-t border-rule">
        <div className="font-medium text-default">{name}</div>
        <div className="text-xs text-muted">{role} · {from}</div>
      </figcaption>
    </figure>
  );
}
